// DatePickerField — reusable MUI X DatePicker для React Hook Form.
// Локаль: ru (date-fns/locale). Формат: dd.MM.yyyy. minDate = today.
import type { ReactNode } from 'react';
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers';
import { TextField } from '@mui/material';
import { parseFieldValue, toIsoDate } from './dateValue';

interface DatePickerFieldProps<T extends FieldValues> {
  /** Имя поля в форме. */
  name: Path<T>;
  /** RHF control. */
  control: Control<T>;
  /** Label поля. */
  label: string;
  /** Обязательное поле. */
  required?: boolean;
  /** Helper text под полем. */
  helperText?: string;
  /** Минимальная допустимая дата (default = today). */
  minDate?: 'today' | Date;
  /** Максимальная допустимая дата. */
  maxDate?: Date;
  /** Disabled. */
  disabled?: boolean;
  /** Полная ширина. */
  fullWidth?: boolean;
  /** ARIA-label для a11y. */
  'aria-label'?: string;
}

/** Поле выбора даты (MUI X DatePicker) для React Hook Form. */
export function DatePickerField<T extends FieldValues>({
  name,
  control,
  label,
  required = false,
  helperText,
  minDate,
  maxDate,
  disabled = false,
  fullWidth = true,
  'aria-label': ariaLabel,
}: DatePickerFieldProps<T>): ReactNode {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const resolvedMinDate = minDate === 'today' ? new Date() : minDate;
        return (
        <DatePicker
          value={parseFieldValue(field.value)}
          onChange={(newValue: Date | null) => {
            field.onChange(toIsoDate(newValue));
          }}
          format="dd.MM.yyyy"
          {...(resolvedMinDate !== undefined && { minDate: resolvedMinDate })}
          {...(maxDate !== undefined && { maxDate })}
          disabled={disabled}
          slotProps={{
            textField: {
              label,
              required,
              fullWidth,
              error: Boolean(fieldState.error),
              helperText: fieldState.error?.message ?? helperText,
              inputProps: ariaLabel ? { 'aria-label': ariaLabel } : undefined,
            },
          }}
        />
        );
      }}
    />
  );
}